import React, { useCallback, useEffect, useState } from 'react'
import Cell from '../widgets/Cell';
import { GiCheckMark } from 'react-icons/gi';
import useReserveEntity from '../../hooks/useReserveEntity';
import useMyFormState from '../../hooks/useMyFormState';
import { isValidPassword, isValidPersonName, isValidUsername } from '../../hooks/useValidation';
import FormButton from '../widgets/FormButton';
import BlinkingCursor from '../widgets/BlinkingCursor';
import JoinErrorPane from './JoinErrorPane';
import CopyToClip from '../../infra/CopyToClip';
import JoinSignLink from './JoinSignLink';
import { joinSignUpUrl } from '../../providers/EnvContextProvider';
import '../widgets/widgets.css'
import './Join.css'

const F_USERNAME = 'username';
const F_PASSWD   = 'password';
const F_FIRST_NAME = 'firstName';

export default function JoinAccepted({joinData, joinCode}) {  

  const { 
    title, 
    note, 
    accountName, 
    sessionKey, 
    firstName:initFirstName, 
    teamName, 
    password:initPassword, 
    username:initUsername = '', 
    // fromFirstName // The Sender
  } = joinData;

  const isPredefinedUsername = initUsername ? true : false;

  const {
    updateFormField, getFormModel,
    handleAddError,
    handleRemoveError,
    submitEnabled,
    setSubmitEnabled,
    locked,
    setLocked, setReload
  } = useMyFormState();  

  const {reserveEntity} = useReserveEntity();
  // const [updatedUsername, setUpdatedUsername] = useState(initUsername);
  // const [updatedPassword, setUpdatedPassword] = useState(initPassword);
  // const [updatedFirstName, setUpdatedFirstName] = useState(initFirstName)
  const [serverMessage, setServerMessage] = useState('');
  const [msgTitle, setMsgTitle] = useState(title);
  const [msgNote, setMsgNote] = useState(note);
  const [errorData, setErrorData] = useState(null);
  const [terminalCommand, setTerminalCommand] = useState('');
  const [signInEnabled, setSignInEnabled] = useState(false);
  
  const [myModel, setMyModel] = useState({});




  const applyUsername = useCallback(async(path, aUsername) => {
    updateFormField(path, aUsername);
    if (isValidUsername(aUsername, true) && Boolean(path === F_USERNAME)) {
      return reserveEntity(path, aUsername);
    }
    return false;
  }, [updateFormField, reserveEntity]);

  const applyPassword = useCallback(async (path, aPassword) => {
    updateFormField(path, aPassword);
    return isValidPassword(aPassword, true);
  }, [updateFormField]);

  const applyFirstName = useCallback(async (path, aFirstName) => {
    updateFormField(path, aFirstName);
    return isValidPersonName(aFirstName);
  },[updateFormField]);

  const appendMsg = useCallback((text) => {
    setServerMessage(prev => {
      return prev ? prev + "  /  " + text : text;
    });
  }, []);

  const onJoinOk = useCallback((response) => {
    if (response.ok) {
      console.log('---[ JoinSignUp OK ]---');
      response.json().then(data => {
        setMsgTitle(data.title);
        setMsgNote(data.note);
        appendMsg('SignUp Completed. Welcome to Kiponos.io! (Join Status: ' + data.joinState + ")");
        setSignInEnabled(true);
      });      
    }
    else {
      response.json().then(error => {
        setMsgTitle(error.title);
        setMsgNote(error.note);
        setErrorData(error);
        appendMsg('SignUp Error: '+JSON.stringify(error.logFinder));
      });      
    }
  },[appendMsg]);

  const onJoinError = useCallback((error) => {
    //console.log('[JoinSignUp] JoinSignUp Error: %s',JSON.stringify(error));
    setErrorData(error);
    appendMsg(error.logFinder);
    return false;
  }, [appendMsg]);

  const handleJoin = useCallback(async (e) => {
    setSubmitEnabled(false);
    setLocked(true);

    const model = getFormModel();    
    setTerminalCommand('full-join username = '+model[F_USERNAME]);
    return fetch(joinSignUpUrl, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        'joinCode': joinCode,
        'sessionKey': sessionKey,
        [F_USERNAME]: model[F_USERNAME],
        [F_PASSWD]: model[F_PASSWD],
        [F_FIRST_NAME]: model[F_FIRST_NAME]
      })
    })
    .then(onJoinOk)
    .catch(onJoinError);
  }, [getFormModel, joinCode, onJoinError, onJoinOk, sessionKey, setLocked, setSubmitEnabled]);

  const usernameLock = locked || isPredefinedUsername;

  const successPane = <div className='joinSuccessPane'>
        <div className='msgSuccess'>        
          {msgTitle}
        </div>
        <div className='welcomeNote'>
          {msgNote}
        </div>
  </div>

  const errorPane = <JoinErrorPane key='joinErrorData-1' errorData={errorData}/>

  const promptComp = <div className='terminalPrompt'>
                      <span style={{color: '#26A060', fontWeight: 'bold'}}>{getFormModel()[F_USERNAME]}@kiponos.io</span> 
                      <span style={{color: '#C8F8FE', fontWeight: 'bold'}}>:</span> 
                      <span style={{color: '#124888', fontWeight: 'bold'}}>~/dos-6.22/organization/{accountName}/team/{teamName}/user/{getFormModel()[F_FIRST_NAME]}</span>
                      <span style={{color: '#C8F8FE', fontWeight: 'bold'}}>$</span>
                      {!terminalCommand && <BlinkingCursor />}
                    </div>;

  useEffect(()=>{
    if (joinData) {
      updateFormField(F_USERNAME, initUsername);
      updateFormField(F_PASSWD, initPassword);
      updateFormField(F_FIRST_NAME, initFirstName);
      setReload(prev => !prev);
      setMyModel({...joinData});
    }
  }, [joinData, initFirstName, initPassword, initUsername, updateFormField, setReload]);

  console.log('Form Model: %s', JSON.stringify(getFormModel(), null, 2));
  
  const modelUsername = myModel[F_USERNAME];  //getFormModel()[F_USERNAME];
  const modelPassword = getFormModel()[F_PASSWD];
  const modelFirstName = getFormModel()[F_FIRST_NAME];

  return (
    <div className='joinAcceptedWrapper'>
      <div className='join'>
        <div className='joinBrand'>
          <div className='brandAccount'>{accountName}</div>
          <div className='brandTeam'>{teamName}</div>
        </div>

        {errorData && errorPane}
        {!errorData && successPane}

        <div className='joinCell'>
          <div className='joinCellHeader'>Username:</div>          
          <Cell path={F_USERNAME}
                isLocked={usernameLock}
                placeholder='Your preferred username' 
                isRequired={true}
                validateMe={isValidUsername} 
                saveMe={applyUsername} 
                addError={handleAddError} 
                removeError={handleRemoveError}
                key='joinUser'>{modelUsername}</Cell>
          <CopyToClip text={modelUsername} />
        </div>
        <div className='joinCell'>
          <div className='joinCellHeader'>Password:</div>
          <Cell path={F_PASSWD}
                placeholder='Your strong password' 
                isRequired={true}
                isLocked={locked}
                validateMe={isValidPassword} 
                saveMe={applyPassword} 
                addError={handleAddError} 
                removeError={handleRemoveError} 
                key='joinPass'>{modelPassword}</Cell>
          <CopyToClip text={modelPassword} />
        </div>
        <div className='joinCell'>
        <div className='joinCellHeader'>First Name:</div>
          <Cell path={F_FIRST_NAME}
                placeholder='Your First Name'
                isRequired={true}
                isLocked={locked}
                validateMe={isValidPersonName}
                saveMe={applyFirstName}
                addError={handleAddError}
                removeError={handleRemoveError}
                key='joinFirstName'>{modelFirstName}</Cell>
          <CopyToClip text={modelFirstName} />
        </div>
        <div className='joinSubmit'>
          <div style={{marginLeft:160}}>

          </div>
          <FormButton isLocked={!submitEnabled}
                      onClick={handleJoin} 
                      key='handleJoin'>Join Kiponos</FormButton>

          {signInEnabled && <JoinSignLink />}
          {!signInEnabled && <div style={{marginRight:160}}></div>}
        </div>
      </div>

      <div className='myTerminalMsg'>
        <div className='msgBar'>awesome config project for {modelFirstName}</div>
        <div className='joinAcceptedMessage'>
          <div className='terminalPrompt'>
            <span style={{color: '#26A060', fontWeight: 'bold'}}>{modelUsername}@kiponos.io</span> 
            <span style={{color: '#C8F8FE', fontWeight: 'bold'}}>:</span> 
            <span style={{color: '#124888', fontWeight: 'bold'}}>~/dos-6.22/organization/{accountName}/team/{teamName}/user/{modelFirstName}</span>
            <span style={{color: '#C8F8FE', fontWeight: 'bold'}}>$</span>
            <span style={{color: '#C8F8FE', fontWeight: 'bold'}}>{terminalCommand}</span>
            {!terminalCommand && <BlinkingCursor />}
          </div>
          <div className='terminalOutput'>
            {serverMessage}
            <span style={{display: 'flex', flexDirection:'row'}}> 
              {terminalCommand && promptComp} {terminalCommand && <BlinkingCursor/>}
            </span>           
          </div>
          
        </div>
      </div>

      <div className='joinAcceptedFooter'>
        <div>
          <GiCheckMark style={{marginRight:16, marginLeft: 24}}/> Invitation Verified
        </div>
        <div>
          Kiponos.io &copy; Copyrights 2022-2025 All Rights Reserved.
        </div>
      </div>
    </div>
  )
}
